/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { PROCESSOR_TYPES } from "./processor-types";
import { log } from "./logger";
import { KEYBINDS, getKeybindDisplay, getKeybindsForMode, type KeybindConfig } from "./keybinds";

/**
 * Mode enumeration corresponding to workflow types
 * K = Classification workflows
 * E = Extraction workflows
 */
export enum HitlMode {
    CLASSIFICATION = "K",
    EXTRACTION = "E"
}

/**
 * Professional keyboard shortcuts manager for HITL frontend
 * Combines the reliability of direct event handling with centralized management
 */
export class HitlKeyboardShortcuts {
    private currentMode: HitlMode | null = null;
    private isActive = false;
    private boundHandler: ((e: KeyboardEvent) => void) | null = null;
    private boundCaptureHandler: ((e: KeyboardEvent) => void) | null = null;

    // Cached DOM references for performance
    private cachedSelectors = {
        mwcTextfield: null as NodeList | null,
        lastCacheTime: 0
    };
    private static readonly CACHE_DURATION = 5000; // 5 seconds

    // Multi-digit class ID input buffer (for IDs > 9)
    private digitBuffer: string = '';
    private digitBufferTimeout: number | null = null;
    private static readonly DIGIT_BUFFER_TIMEOUT_MS = 800; // 800ms timeout between digits

    // Action handlers - set by the component
    private handlers = {
        sendToClearing: null as (() => void) | null,
        sendToIrrlaeufer: null as (() => void) | null,
        deleteCase: null as (() => void) | null,
        deleteExtractedFields: null as (() => void) | null,
        exitWithoutSaving: null as (() => void) | null,
        nextFieldOrCompleteCase: null as (() => void) | null,
        clickSaveButton: null as (() => void) | null,
        selectClassById: null as ((classId: string) => void) | null,
        showDigitBufferFeedback: null as ((buffer: string) => void) | null,
        hideDigitBufferFeedback: null as (() => void) | null,
        zoomIn: null as (() => void) | null,
        zoomOut: null as (() => void) | null,
        zoomToFit: null as (() => void) | null,
        undo: null as (() => void) | null,
        redo: null as (() => void) | null
    };

    constructor() {
        this.boundHandler = this.handleKeyDown.bind(this);
        this.boundCaptureHandler = this.handleKeyDownCapture.bind(this);
    }

    /**
     * Capture-phase handler to block ALL keyboard events when dialog is open
     * This intercepts events before they reach the Google AI UI component
     */
    private handleKeyDownCapture(e: KeyboardEvent): void {
        if (this.isDialogOpen()) {
            // Allow Tab for accessibility navigation within dialog
            if (e.key === 'Tab') {
                return;
            }
            // Allow Escape to close dialog
            if (e.key === 'Escape') {
                return;
            }
            // Allow Enter to submit forms in dialog
            if (e.key === 'Enter') {
                return;
            }
            // Block events from reaching AI UI component (prevents [ ] page navigation)
            e.stopPropagation();
        }
    }

    /**
     * Initialize shortcuts with handler functions
     */
    initialize(handlers: {
        sendToClearing: () => void;
        sendToIrrlaeufer: () => void;
        deleteCase: () => void;
        deleteExtractedFields: () => void;
        exitWithoutSaving: () => void;
        nextFieldOrCompleteCase: () => void;
        clickSaveButton: () => void;
        selectClassById: (classId: string) => void;
        showDigitBufferFeedback: (buffer: string) => void;
        hideDigitBufferFeedback: () => void;
        zoomIn: () => void;
        zoomOut: () => void;
        zoomToFit: () => void;
        undo: () => void;
        redo: () => void;
    }): void {
        this.handlers = handlers;

        // Expose to window for debugging in development environments
        if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
            window.hitlKeyboardShortcuts = this;
        }
    }

    /**
     * Set the current mode based on processor type
     */
    setModeFromProcessorType(processorType: string): void {
        let newMode: HitlMode | null = null;

        if (processorType === PROCESSOR_TYPES.CLASSIFICATION || processorType === "DAI_COMPLEX_CLASSIFICATION") {
            newMode = HitlMode.CLASSIFICATION;
        } else if (processorType === PROCESSOR_TYPES.EXTRACTION || processorType === "Entity") {
            newMode = HitlMode.EXTRACTION;
        } else if (processorType === PROCESSOR_TYPES.SPLITTING || processorType === "DocSplitter") {
            newMode = HitlMode.EXTRACTION; // Splitting is treated as extraction mode
        }

        if (newMode !== this.currentMode) {
            this.currentMode = newMode;
            log.debug(`Keyboard shortcuts mode set to ${newMode || 'none'} for processor type: ${processorType}`);
        }
    }

    /**
     * Activate keyboard shortcuts
     */
    activate(): void {
        if (!this.isActive && this.boundHandler && this.boundCaptureHandler) {
            // Capture-phase handler intercepts events BEFORE they reach AI UI component
            window.addEventListener('keydown', this.boundCaptureHandler, { capture: true });
            // Bubble-phase handler for HITL-specific shortcuts
            window.addEventListener('keydown', this.boundHandler);
            this.isActive = true;
        }
    }

    /**
     * Deactivate keyboard shortcuts
     */
    deactivate(): void {
        if (this.isActive && this.boundHandler && this.boundCaptureHandler) {
            window.removeEventListener('keydown', this.boundCaptureHandler, { capture: true });
            window.removeEventListener('keydown', this.boundHandler);
            this.isActive = false;
        }
    }

    /**
     * Get cached mwc-textfield elements or refresh cache if stale
     */
    private getCachedMwcTextfields(): NodeList {
        const now = Date.now();
        if (!this.cachedSelectors.mwcTextfield || (now - this.cachedSelectors.lastCacheTime) > HitlKeyboardShortcuts.CACHE_DURATION) {
            this.cachedSelectors.mwcTextfield = document.querySelectorAll('mwc-textfield');
            this.cachedSelectors.lastCacheTime = now;
        }
        return this.cachedSelectors.mwcTextfield;
    }

    /**
     * Check if target is within a cached mwc-textfield
     */
    private isInMwcTextfield(target: HTMLElement): boolean {
        const cachedElements = this.getCachedMwcTextfields();
        for (let i = 0; i < cachedElements.length; i++) {
            if (cachedElements[i].contains(target)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Check if a dialog is currently open
     * Uses multiple detection methods for reliability
     */
    private isDialogOpen(): boolean {
        const dialogElement = document.getElementById('dialog');
        return !!(
            document.querySelector('mwc-dialog[open]') ||
            document.querySelector('#dialog[open="true"]') ||
            (dialogElement && dialogElement.hasAttribute('open')) ||
            (dialogElement && (dialogElement as any).open === true)
        );
    }

    /**
     * Check if a keyboard event matches a keybind configuration
     * Supports both character-based (event.key) and position-based (event.code) matching
     */
    private matchesKeybind(e: KeyboardEvent, config: KeybindConfig): boolean {
        // Match by character (event.key) if configured - works across keyboard layouts
        if (config.key) {
            // Case-insensitive character matching
            if (e.key.toLowerCase() !== config.key.toLowerCase()) {
                return false;
            }
        }
        // Match by physical position (event.code) if configured - for special keys
        else if (config.code) {
            const codes = Array.isArray(config.code) ? config.code : [config.code];
            if (!codes.includes(e.code)) {
                return false;
            }
        }
        else {
            // No key or code specified
            return false;
        }

        // Check modifiers
        const hasCtrl = e.ctrlKey || e.metaKey;
        const hasAlt = e.altKey;
        const hasShift = e.shiftKey;

        const needsCtrl = config.modifiers.ctrl || false;
        const needsAlt = config.modifiers.alt || false;
        const needsShift = config.modifiers.shift || false;

        return hasCtrl === needsCtrl && hasAlt === needsAlt && hasShift === needsShift;
    }

    /**
     * Main keyboard event handler - configuration-driven approach
     */
    private handleKeyDown(e: KeyboardEvent): void {
        // Completely disable keyboard shortcuts when a dialog is open
        // This ensures users can type freely (including umlauts) in dialog inputs
        if (this.isDialogOpen()) {
            return;
        }

        // Debug logging for keyboard layout detection
        // Shows both the character produced (key) and physical position (code)
        if (e.altKey || (e.ctrlKey && !['Control', 'Meta'].includes(e.key))) {
            log.debug(`[Keyboard] key="${e.key}" code="${e.code}" | Alt:${e.altKey} Ctrl:${e.ctrlKey} Shift:${e.shiftKey} Meta:${e.metaKey}`);
        }

        // Don't handle shortcuts if user is typing in input fields
        const target = e.target as HTMLElement;
        const isInputField = target && (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable ||
            this.isInMwcTextfield(target)
        );

        // Try to match against all configured keybinds
        for (const [key, config] of Object.entries(KEYBINDS)) {
            if (!this.matchesKeybind(e, config)) {
                continue;
            }

            // Check if shortcut should be blocked in input fields
            if (isInputField && config.blockInInputFields !== false) {
                // Special case: NEXT_FIELD_OR_COMPLETE (Enter) needs custom handling
                if (config.action === 'nextFieldOrCompleteCase') {
                    // Check if we're in a dialog - use multiple detection methods
                    const dialogElement = document.getElementById('dialog');
                    const inDialog = target.closest('mwc-dialog') ||
                        target.closest('[role="dialog"]') ||
                        target.closest('#dialog') ||
                        document.querySelector('mwc-dialog[open]') ||
                        document.querySelector('#dialog[open="true"]') ||
                        (dialogElement && dialogElement.hasAttribute('open')) ||
                        (dialogElement && (dialogElement as any).open === true);

                    if (inDialog) {
                        // We're in a dialog - let normal form submission work
                        continue;
                    }

                    // Not in dialog and in extraction mode - allow it
                    if (this.currentMode !== HitlMode.EXTRACTION) {
                        continue;
                    }
                } else {
                    continue;
                }
            }

            // Check mode availability
            if (config.modes && config.modes.length > 0) {
                if (!this.currentMode || !config.modes.includes(this.currentMode)) {
                    continue;
                }
            }

            // Match found! Execute the handler
            e.preventDefault();
            const handler = (this.handlers as any)[config.action];
            if (handler) {
                handler();
            } else {
                log.warn(`Handler not found for action: ${config.action}`);
            }
            return;
        }

        // Number keys (0-9): Accumulate digits for classification class ID (K mode only)
        // Supports multi-digit IDs (e.g., "12", "345") with 800ms timeout between digits
        // Don't trigger if modifier keys are pressed or if a dialog is open
        if (this.currentMode === HitlMode.CLASSIFICATION && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
            const numberKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
            if (numberKeys.includes(e.key)) {
                // Only trigger if not in an input field and no dialog is open
                if (!isInputField && !this.isDialogOpen()) {
                    e.preventDefault();
                    this.accumulateDigit(e.key);
                    return;
                }
            }
        }

        // Enter: Confirm digit buffer (K mode only)
        // Don't trigger if a dialog is open
        if (this.currentMode === HitlMode.CLASSIFICATION && e.key === 'Enter' && this.digitBuffer.length > 0 && !isInputField && !this.isDialogOpen()) {
            e.preventDefault();
            this.confirmDigitBuffer();
            return;
        }

        // Escape: Cancel digit buffer (K mode only)
        // Don't trigger if a dialog is open
        if (this.currentMode === HitlMode.CLASSIFICATION && e.key === 'Escape' && this.digitBuffer.length > 0 && !isInputField && !this.isDialogOpen()) {
            e.preventDefault();
            this.cancelDigitBuffer();
            return;
        }
    }

    /**
     * Get current mode for debugging
     */
    getCurrentMode(): HitlMode | null {
        return this.currentMode;
    }

    /**
     * Get available shortcuts for current mode (configuration-driven)
     */
    getAvailableShortcuts(): string[] {
        const shortcuts: string[] = [];

        // Get keybinds for current mode from configuration
        const availableKeybinds = getKeybindsForMode(this.currentMode);

        for (const config of availableKeybinds) {
            const display = getKeybindDisplay(config);
            shortcuts.push(`${display} - ${config.description}`);
        }

        // Add classification-specific shortcuts (digit buffer)
        if (this.currentMode === HitlMode.CLASSIFICATION) {
            shortcuts.push('0-9 - Select Classification Class by ID (multi-digit supported)');
            shortcuts.push('Enter - Confirm class ID selection');
            shortcuts.push('Escape - Cancel class ID input');
        }

        return shortcuts;
    }

    /**
     * Accumulate digit for multi-digit class ID input
     */
    private accumulateDigit(digit: string): void {
        // Clear existing timeout
        if (this.digitBufferTimeout !== null) {
            window.clearTimeout(this.digitBufferTimeout);
        }

        // Add digit to buffer
        this.digitBuffer += digit;

        // Show visual feedback
        this.handlers.showDigitBufferFeedback?.(this.digitBuffer);

        // Set timeout to auto-confirm after inactivity
        this.digitBufferTimeout = window.setTimeout(() => {
            this.confirmDigitBuffer();
        }, HitlKeyboardShortcuts.DIGIT_BUFFER_TIMEOUT_MS);
    }

    /**
     * Confirm and execute digit buffer selection
     */
    private confirmDigitBuffer(): void {
        if (this.digitBuffer.length === 0) {
            return;
        }

        // Clear timeout
        if (this.digitBufferTimeout !== null) {
            window.clearTimeout(this.digitBufferTimeout);
            this.digitBufferTimeout = null;
        }

        // Execute selection with accumulated buffer
        const classId = this.digitBuffer;
        this.handlers.selectClassById?.(classId);

        // Hide feedback
        this.handlers.hideDigitBufferFeedback?.();

        // Clear buffer
        this.digitBuffer = '';
    }

    /**
     * Cancel digit buffer input
     */
    private cancelDigitBuffer(): void {
        // Clear timeout
        if (this.digitBufferTimeout !== null) {
            window.clearTimeout(this.digitBufferTimeout);
            this.digitBufferTimeout = null;
        }

        // Hide feedback
        this.handlers.hideDigitBufferFeedback?.();

        // Clear buffer
        this.digitBuffer = '';
    }

}

/**
 * Global instance for use across the application
 */
export const hitlKeyboardShortcuts = new HitlKeyboardShortcuts();

// Expose to window for debugging
declare global {
    interface Window {
        hitlKeyboardShortcuts: HitlKeyboardShortcuts;
    }
}
// Window assignment happens during initialization to ensure proper timing
